import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  HardHat, Plus, Search, Eye, Pencil, Trash2, Loader2,
  CheckCircle2, XCircle, AlertTriangle, Clock, Send,
  ChevronRight, Building2, MapPin, Filter, FileDown, Timer, Users, Megaphone
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  brouillon:  { label: 'Brouillon',   icon: Clock,          cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  en_cours:   { label: 'En cours',    icon: Pencil,         cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  soumis:     { label: 'Soumis',      icon: Send,           cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  valide:     { label: 'Validé',      icon: CheckCircle2,   cls: 'bg-green-100 text-green-700 border-green-300' },
  refuse:     { label: 'Refusé',      icon: XCircle,        cls: 'bg-red-100 text-red-700 border-red-300' },
  a_corriger: { label: 'À corriger',  icon: AlertTriangle,  cls: 'bg-orange-100 text-orange-700 border-orange-300' },
};

export default function ChantierListPage() {
  const { token, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [chantiers, setChantiers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filterOrg, setFilterOrg] = useState('');
  const [orgs, setOrgs] = useState([]);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = async () => {
    try {
      const fetches = [
        fetch(`${API}/api/chantiers`, { headers }),
        fetch(`${API}/api/chantiers/dashboard-stats`, { headers }),
      ];
      if (isAdmin) fetches.push(fetch(`${API}/api/organisations`, { headers }));
      const responses = await Promise.all(fetches);
      if (responses[0].ok) setChantiers(await responses[0].json());
      if (responses[1].ok) setStats(await responses[1].json());
      if (isAdmin && responses[2]?.ok) setOrgs(await responses[2].json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  const filtered = useMemo(() => {
    let list = chantiers;
    if (filterStatus) list = list.filter(c => c.status === filterStatus);
    if (filterOrg) list = list.filter(c => c.organisation_id === filterOrg);
    if (searchQ) {
      const q = searchQ.toLowerCase();
      list = list.filter(c =>
        (c.nom_magasin || '').toLowerCase().includes(q) ||
        (c.enseigne || '').toLowerCase().includes(q) ||
        (c.ville || '').toLowerCase().includes(q) ||
        (c.created_by_name || '').toLowerCase().includes(q) ||
        (c.organisation_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [chantiers, filterStatus, filterOrg, searchQ]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/chantiers/${deleteTarget.id}`, { method: 'DELETE', headers });
      if (res.ok) {
        toast.success('Chantier supprimé');
        setDeleteTarget(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setDeleting(false); }
  };

  const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return '-'; }
  };

  const handleExportPdf = async (chId, nomMagasin, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${API}/api/chantiers/${chId}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PV_${(nomMagasin || 'chantier').replace(/\s/g, '_')}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF téléchargé');
      } else { toast.error('Erreur export PDF'); }
    } catch { toast.error('Erreur réseau'); }
  };

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <HardHat className="w-8 h-8 text-orange-500" />
              Pilotage Chantier
            </h1>
            <p className="text-sm text-slate-500 mt-1">Suivi & Validation des corners</p>
          </div>
          <Button
            onClick={() => navigate('/chantier/new')}
            className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
            data-testid="new-chantier-btn"
          >
            <Plus className="w-4 h-4" /> Nouveau chantier
          </Button>
        </div>

        {/* Dashboard KPIs */}
        {stats && (
          <div className="space-y-4">
            {/* Row 1: Main KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="border-slate-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900" data-testid="kpi-total">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total chantiers</p>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50/50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-red-700" data-testid="kpi-repassage">{stats.repassage_count || 0}</p>
                  <p className="text-xs text-red-600">Nouveau passage</p>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-700" data-testid="kpi-soumis">{stats.status_counts?.soumis || 0}</p>
                  <p className="text-xs text-amber-600">En attente validation</p>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50/50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-700" data-testid="kpi-valides">{stats.status_counts?.valide || 0}</p>
                  <p className="text-xs text-green-600">Validés</p>
                </CardContent>
              </Card>
            </div>

            {/* Row 2: Statuts détaillés + Délai + Orgs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Status breakdown */}
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5" /> Répartition par statut
                  </p>
                  <div className="space-y-2">
                    {Object.entries(STATUS_CFG).map(([key, cfg]) => {
                      const count = stats.status_counts?.[key] || 0;
                      if (count === 0 && key !== 'brouillon' && key !== 'soumis') return null;
                      const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                      return (
                        <div key={key} className="flex items-center justify-between text-sm" data-testid={`status-bar-${key}`}>
                          <span className="text-slate-600 flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${key === 'brouillon' ? 'bg-slate-400' : key === 'soumis' ? 'bg-amber-400' : key === 'valide' ? 'bg-green-500' : key === 'refuse' ? 'bg-red-500' : key === 'a_corriger' ? 'bg-orange-400' : 'bg-blue-400'}`} />
                            {cfg.label}
                          </span>
                          <span className="font-semibold text-slate-800">{count} <span className="text-xs text-slate-400 font-normal">({pct}%)</span></span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Délai moyen */}
              <Card className="border-blue-200 bg-blue-50/30">
                <CardContent className="p-4 flex flex-col items-center justify-center h-full">
                  <Timer className="w-6 h-6 text-blue-500 mb-2" />
                  <p className="text-3xl font-bold text-blue-700" data-testid="kpi-delai">
                    {stats.delai_moyen_jours != null ? `${stats.delai_moyen_jours}j` : '—'}
                  </p>
                  <p className="text-xs text-blue-600 mt-1 text-center">Délai moyen création → soumission</p>
                </CardContent>
              </Card>

              {/* Par organisation */}
              {isAdmin && stats.by_org && Object.keys(stats.by_org).length > 0 && (
                <Card className="border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" /> Par organisation
                    </p>
                    <div className="space-y-2">
                      {Object.entries(stats.by_org).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([orgName, count]) => {
                        const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                        return (
                          <div key={orgName} className="flex items-center justify-between text-sm" data-testid={`org-bar-${orgName}`}>
                            <span className="text-slate-600 truncate mr-2">{orgName}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-orange-400 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="font-semibold text-slate-800 text-xs w-6 text-right">{count}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
              {/* Fallback for non-admin or no orgs */}
              {(!isAdmin || !stats.by_org || Object.keys(stats.by_org).length === 0) && (
                <Card className="border-slate-200">
                  <CardContent className="p-4 flex flex-col items-center justify-center h-full">
                    <p className="text-3xl font-bold text-slate-700" data-testid="kpi-brouillons">{stats.status_counts?.brouillon || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">Brouillons en cours</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Search & Filters - mobile-first layout */}
        <div className="space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:gap-3 sm:items-center">
          <div className="relative sm:flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Rechercher par magasin, enseigne, ville…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              className="pl-10"
              data-testid="chantier-search"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 text-sm border rounded-md px-3 bg-white dark:bg-slate-800 w-full"
              data-testid="chantier-filter-status"
            >
              <option value="">Tous statuts</option>
              {Object.entries(STATUS_CFG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            {isAdmin && orgs.length > 0 && (
              <select
                value={filterOrg}
                onChange={(e) => setFilterOrg(e.target.value)}
                className="h-10 text-sm border rounded-md px-3 bg-white dark:bg-slate-800 w-full"
                data-testid="chantier-filter-org"
              >
                <option value="">Toutes orgas</option>
                {orgs.map(o => (
                  <option key={o.id} value={o.id}>{o.nom}</option>
                ))}
              </select>
            )}
          </div>
        </div>
        {(searchQ || filterStatus || filterOrg) && (
          <p className="text-xs text-slate-500" data-testid="chantier-result-count">
            {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
          </p>
        )}

        {/* Chantier List */}
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <HardHat className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">
                {chantiers.length === 0 ? "Aucun chantier pour le moment" : "Aucun résultat trouvé"}
              </p>
              {chantiers.length === 0 && (
                <Button onClick={() => navigate('/chantier/new')} className="mt-4 bg-orange-500 hover:bg-orange-600 text-white gap-2">
                  <Plus className="w-4 h-4" /> Créer mon premier chantier
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="chantier-list">
            {filtered.map((ch) => {
              const stCfg = STATUS_CFG[ch.status] || STATUS_CFG.brouillon;
              const StIcon = stCfg.icon;
              return (
                <Card
                  key={ch.id}
                  className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-orange-400"
                  onClick={() => navigate(`/chantier/${ch.id}`)}
                  data-testid={`chantier-card-${ch.id}`}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Mobile: title + status side-by-side at top */}
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                              {ch.nom_magasin || 'Sans nom'}
                            </h3>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {ch.marques?.slice(0, 2).map(m => (
                                <Badge key={m} variant="outline" className="text-[10px] px-1.5 py-0">{m}</Badge>
                              ))}
                              {ch.repassage_prevu === 'Oui' && (
                                <Badge className="bg-red-100 text-red-700 border-red-300 text-[10px] gap-1 px-1.5 py-0">
                                  <AlertTriangle className="w-3 h-3" /> Nouveau passage
                                </Badge>
                              )}
                            </div>
                          </div>
                          {/* Status badge always visible top-right (mobile + desktop hidden, shown on mobile) */}
                          <div className="flex flex-col items-end gap-1 shrink-0 sm:hidden">
                            <Badge variant="outline" className={`${stCfg.cls} gap-1 whitespace-nowrap text-[10px] px-1.5 py-0`}>
                              <StIcon className="w-3 h-3" />{stCfg.label}
                            </Badge>
                            {ch.pv_diffuse && (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 gap-1 text-[10px] px-1.5 py-0" data-testid={`diffused-badge-${ch.id}`}>
                                <Megaphone className="w-3 h-3" />Diffusé
                              </Badge>
                            )}
                          </div>
                        </div>
                        {/* Meta info */}
                        <div className="flex items-center gap-x-3 gap-y-1 mt-2 text-xs sm:text-sm text-slate-500 flex-wrap">
                          {ch.enseigne && <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{ch.enseigne}</span>}
                          {ch.ville && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{ch.ville}</span>}
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{fmtDate(ch.created_at)}</span>
                          {isAdmin && ch.organisation_name && <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-600 border-orange-200 px-1.5 py-0"><HardHat className="w-3 h-3 mr-0.5" />{ch.organisation_name}</Badge>}
                          {isAdmin && ch.created_by_name && <span className="truncate max-w-[140px]">par {ch.created_by_name}</span>}
                        </div>
                      </div>
                      {/* Right side: status + actions (desktop only on large screens) */}
                      <div className="hidden sm:flex items-center gap-3 shrink-0">
                        <Badge variant="outline" className={`${stCfg.cls} gap-1`}>
                          <StIcon className="w-3 h-3" />{stCfg.label}
                        </Badge>
                        {ch.pv_diffuse && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 gap-1 text-[10px]">
                            <Megaphone className="w-3 h-3" />Diffusé
                          </Badge>
                        )}
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-600 hover:bg-slate-50"
                            onClick={(e) => handleExportPdf(ch.id, ch.nom_magasin, e)} title="Exporter PDF"
                            data-testid={`pdf-chantier-${ch.id}`}
                          ><FileDown className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
                            onClick={() => navigate(`/chantier/${ch.id}`)} title="Voir"
                            data-testid={`view-chantier-${ch.id}`}
                          ><Eye className="w-4 h-4" /></Button>
                          {(ch.status !== 'valide') && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-50"
                              onClick={() => navigate(`/chantier/${ch.id}/edit`)} title="Modifier"
                              data-testid={`edit-chantier-${ch.id}`}
                            ><Pencil className="w-4 h-4" /></Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                            onClick={() => setDeleteTarget(ch)} title="Supprimer"
                            data-testid={`delete-chantier-${ch.id}`}
                          ><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                      {/* Mobile actions row (full-width buttons with labels) */}
                      <div className="grid grid-cols-4 gap-1.5 sm:hidden pt-2 border-t border-slate-100" onClick={e => e.stopPropagation()}>
                        <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-slate-600"
                          onClick={(e) => handleExportPdf(ch.id, ch.nom_magasin, e)}
                          data-testid={`pdf-chantier-${ch.id}`}
                        ><FileDown className="w-3.5 h-3.5" /> PDF</Button>
                        <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-blue-600 border-blue-200"
                          onClick={() => navigate(`/chantier/${ch.id}`)}
                          data-testid={`view-chantier-${ch.id}`}
                        ><Eye className="w-3.5 h-3.5" /> Voir</Button>
                        {(ch.status !== 'valide') ? (
                          <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-amber-600 border-amber-200"
                            onClick={() => navigate(`/chantier/${ch.id}/edit`)}
                            data-testid={`edit-chantier-${ch.id}`}
                          ><Pencil className="w-3.5 h-3.5" /> Modif.</Button>
                        ) : <span />}
                        <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-red-500 border-red-200"
                          onClick={() => setDeleteTarget(ch)}
                          data-testid={`delete-chantier-${ch.id}`}
                        ><Trash2 className="w-3.5 h-3.5" /> Suppr.</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-chantier-modal">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2"><Trash2 className="w-5 h-5" />Supprimer le chantier</DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <p className="text-sm text-slate-600">
              Supprimer <strong>{deleteTarget.nom_magasin} — {deleteTarget.ville}</strong> ? Cette action est irréversible.
            </p>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white gap-1" onClick={handleDelete} disabled={deleting}
              data-testid="confirm-delete-chantier"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </AppLayout>
  );
}
